
<template>
  <FormGroup :title="label" :titleSuffix="labelSuffix" :help="help" :required="required" :slim="noFormGroup">
    <label>
        {{$t('Zoek je adres')}}
      </label>
      <div class="autocomplete-address">
        <div ref="autocompleteContainer" class="autocomplete-address__field"></div>
        <!-- PlaceAutocompleteElement can't be required, this hidden input keeps the native form validation -->
        <input
          class="autocomplete-address__required"
          tabindex="-1"
          aria-hidden="true"
          :required="isAutocompleteRequired"
        />
      </div>
      <Grid>
        <Button v-if="!addressNotFound && !localValue.street && !localValue.city" type="button" warning fill @click="addressNotFound = true" class="not-found-button">Adres niet gevonden?</Button>
      </Grid>
    <Grid v-if="localValue.street || localValue.city || addressNotFound" class="address" :columns="{ tablet: 4 }">
      <Column :span="2">
        <Input
          :value="localValue.street"
          v-model="localValue.street"
          :label="$t('Straat')"
          :id="`${id}-street`"
          :required="isRequired"
          :v="isRequired && v?.street"
          @input="emitAddress"
        />
      </Column>
      <Column>
        <Input
          v-model="localValue.streetNumber"
          :label="$t('Nummer')"
          :id="`${id}-streetNumber`"
          type="text"
          :placeholder="$t('Nummer')"
          :v="isRequired && v?.streetNumber"
          :required="isRequired && !localValue.street"
          @input="emitAddress"
        />
      </Column>
      <Column>
        <Input
          v-model="localValue.bus"
          :label="$t('Bus')"
          :id="`${id}-bus`"
          type="text"
          :placeholder="$t('Bus')"
          @input="emitAddress"
        />
      </Column>
      <Column>
        <Input
          v-model="localValue.postal"
          :label="$t('Postcode')"
          :placeholder="$t('Postcode')"
          :v="isRequired && v?.postal"
          :id="`${id}-postal`"
          :required="isRequired"
          @input="emitAddress"
        />
      </Column>
      <Column :span="2">
        <Input
          v-model="localValue.city"
          :label="$t('Stad')"
          :placeholder="$t('Stad')"
          :required="isRequired"
          :id="`${id}-city`"
          :v="isRequired && v?.city"
          @input="emitAddress"
        />
      </Column>
      <Column>
        <Input
          v-model="localValue.country"
          label="Land"
          placeholder="Land"
          :id="`${id}-country`"
          type="text"
          :v="isRequired && v?.country"
          @input="emitAddress"
          :required="isRequired"
        />
      </Column>
    </Grid>
  </FormGroup>
</template>
<script setup>

import Column from "@/components/Core/Column.vue";
import Grid from "@/components/Core/Grid.vue";
import FormGroup from "@/components/Core/FormGroup.vue";
import Input from "@/components/Core/Input.vue";
import Button from "@/components/Core/Button.vue";
import {ref, onMounted, reactive, computed, nextTick, getCurrentInstance} from 'vue'
import { required } from 'vuelidate/lib/validators'

const addressNotFound = ref(false)
let autocomplete = ref(false)
const autocompleteContainer = ref(null)
const instance = getCurrentInstance()

let localValue = reactive({
  street: '',
  streetNumber: '',
  bus: '',
  city: '',
  postal: '',
  country: ''
})

const { value, label,labelSuffix, isRequired, help, id } = defineProps({
  value: {
    type: [Object],
    default: () => ({
      street: '',
      streetNumber: '',
      bus: '',
      city: '',
      postal: '',
      country: ''
    })
  },
  label: {
    type: String
  },
  labelSuffix: {
    type: String
  },
  isRequired: {
    type: Boolean
  },
  help: {
    type: String
  },
  id: {
    type: String
  },
  v: Object,
  noFormGroup: Boolean
})
const emit = defineEmits(['input'])
// Conditional validations
const validations = computed(() => {
  if (isRequired) {
    return {
      localValue: {
        street: { required },
        streetNumber: { required },
        postal: { required },
        city: { required },
        country: { required }
      }
    }
  }
  return {} // No validations when not required
})

const isAutocompleteRequired = computed(() => {
  return isRequired && !localValue.street && !localValue.city && !addressNotFound;
})

// load Google Maps
onMounted(async () => {
  await nextTick()
  await initMap()
  if ( value && value.street) {
    localValue.street = value.street
    localValue.streetNumber = value.streetNumber
    localValue.bus = value.bus
    localValue.city = value.city
    localValue.postal = value.postal
    localValue.country = value.country
  }
})

const resolveAutocompleteContainer = () => {
  if (autocompleteContainer.value instanceof HTMLElement) {
    return autocompleteContainer.value
  }

  const rootElement = instance?.proxy?.$el
  if (rootElement instanceof HTMLElement) {
    return rootElement.querySelector('.autocomplete-address__field')
  }

  return null
}

const initMap = async () => {
  const container = resolveAutocompleteContainer()
  if (!(container instanceof HTMLElement)) {
    console.warn('AutocompleteAddress could not resolve a container element for Google Places.', container)
    return
  }

  const { PlaceAutocompleteElement } = await google.maps.importLibrary('places')
  autocomplete.value = new PlaceAutocompleteElement({
    includedRegionCodes: ["be", "nl", "fr"],
    // The new element has no 'address' type collection, these primary types cover the same precise-address results
    includedPrimaryTypes: ["street_address", "premise", "subpremise", "route"]
  })
  autocomplete.value.addEventListener("gmp-select", fillInAddress)
  container.appendChild(autocomplete.value)
}
const fillInAddress = async ({ placePrediction }) => {
  const place = placePrediction.toPlace()
  await place.fetchFields({ fields: ["addressComponents"] })
  if (!place.addressComponents) return
  localValue.street = ''
  localValue.streetNumber = ''
  localValue.bus = ''
  localValue.postal = ''
  localValue.city = ''
  localValue.country = ''
  for (const component of place.addressComponents) {
    if (component.types.includes('street_number')) {
        const numberAndBus = component.longText.split(' ')
        localValue.streetNumber = numberAndBus[0]
        localValue.bus = numberAndBus[1]
      } else if (component.types.includes('route')) {
        localValue.street = component.longText
      } else if (component.types.includes('postal_code')) {
        localValue.postal = component.longText
      } else if (component.types.includes('sublocality')||component.types.includes('locality')) {
        localValue.city = component.longText
      } else if (component.types.includes('country')) {
        localValue.country = component.shortText
      }
  }
  emitAddress()
}

const emitAddress = () => {
  emit('input', {
    street: localValue.street,
    streetNumber: localValue.streetNumber,
    bus: localValue.bus,
    city: localValue.city,
    postal: localValue.postal,
    country: localValue.country
  })
}

</script>

<style scoped>
.not-found-button {
  margin-left: auto;
  margin-top: 0.5rem;
}
.address {
  margin-top: 1rem;
}
</style>
<style>
.autocomplete-address {
  position: relative;
}
/* Match the Input component, the element renders its own input in a shadow DOM */
.autocomplete-address gmp-place-autocomplete {
  width: 100%;
  height: 2.45rem;
  color-scheme: light;
  background-color: var(--primary-bg);
  color: var(--primary-text-color);
  border: 1px solid var(--gray-darker);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 1rem;
}
.app.dark-mode .autocomplete-address gmp-place-autocomplete {
  color-scheme: dark;
}
.autocomplete-address gmp-place-autocomplete::part(focus-ring) {
  border-radius: var(--border-radius);
}
.autocomplete-address__required {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
</style>
